import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, HostListener } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ProjectTaskService } from 'src/app/private/services/project-task.service';
import { BugService } from 'src/app/private/services/bug.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-project-detail-kanban',
  templateUrl: './project-detail-kanban.component.html',
  styleUrls: ['./project-detail-kanban.component.scss']
})
export class ProjectDetailKanbanComponent implements OnInit, OnChanges {
  @Input() projectId: any;
  @Input() selectedItem: any = null;
  @Input() projectMembers: any[] = [];
  @Input() currentUserId: any;
  @Input() sprints: any[] = [];

  @Output() taskSelected = new EventEmitter<any>();
  @Output() taskEdit = new EventEmitter<any>();
  @Output() taskDelete = new EventEmitter<{ task: any; event: Event }>();
  @Output() addTaskClicked = new EventEmitter<void>();
  @Output() addBugClicked = new EventEmitter<void>();
  @Output() taskStatusToggled = new EventEmitter<any>();

  allProjectTasks: any[] = [];
  unfilteredProjectTasks: any[] = [];
  loadingAllTasks = false;
  loadingMore = false;
  kanbanPage = 1;
  kanbanLimit = 20;
  kanbanTotal = 0;
  hasMoreKanban = false;
  cardStatusDdlOpenTaskId: any = null;

  taskSearchQuery = '';
  priorityFilter = 'all';
  assigneeFilter = 'all';
  onlyMyTasks = false;
  onlyOverdue = false;
  sprintFilter = 'all';

  activeBoardTab: 'tasks' | 'bugs' = 'tasks';
  boardStatuses: string[] = [];

  get statuses(): string[] {
    return this.boardStatuses;
  }

  updateBoardStatuses() {
    if (this.activeBoardTab === 'bugs') {
      this.boardStatuses = ['open', 're-open', 'in_progress', 'in_review', 'ready_for_test', 'rejected', 'closed'];
    } else {
      this.boardStatuses = ['todo', 'in_progress', 'in_review', 'blocked', 'completed', 'cancelled'];
    }
  }

  setBoardTab(tab: 'tasks' | 'bugs') {
    this.activeBoardTab = tab;
    this.updateBoardStatuses();
    this.closeCardStatusDdl();
    this.onFilterChange();
  }

  getSprintName(sprintId: any): string {
    const sprint = (this.sprints || []).find(s => Number(s.sprint_id) === Number(sprintId));
    return sprint ? sprint.name : `Sprint ${sprintId}`;
  }

  constructor(
    private taskService: ProjectTaskService,
    private bugService: BugService,
    private localStorage: LocalStorageService,
    private toastr: ToastrService
  ) {}

  ngOnInit() {
    this.updateBoardStatuses();
    this.loadTasks();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectId'] && !changes['projectId'].firstChange) {
      this.updateBoardStatuses();
      this.loadTasks();
    }
  }

  loadTasks() {
    this.loadingAllTasks = true;
    this.kanbanPage = 1;
    this.allProjectTasks = [];
    this.unfilteredProjectTasks = [];
    this._fetchKanbanPage(false);
  }

  loadMoreTasks() {
    if (!this.hasMoreKanban || this.loadingMore || this.loadingAllTasks) return;
    this.loadingMore = true;
    this.kanbanPage++;
    this._fetchKanbanPage(true);
  }

  private _fetchKanbanPage(append: boolean) {
    if (!append) this.loadingAllTasks = true;
    else this.loadingMore = true;

    if (this.activeBoardTab === 'tasks') {
      // 1. Task filters
      const taskWhereClause: any = {
        project_id: this.projectId,
        client_id: this.localStorage.getClientId()
      };
      if (this.priorityFilter && this.priorityFilter !== 'all') {
        taskWhereClause.priority = this.priorityFilter;
      }
      if (this.assigneeFilter && this.assigneeFilter !== 'all') {
        taskWhereClause.assignee_id = this.assigneeFilter;
      }
      if (this.onlyMyTasks && this.currentUserId) {
        taskWhereClause.assignee_id = this.currentUserId;
      }

      const taskPayload: any = {
        user_id: this.localStorage.getUserId(),
        client_id: this.localStorage.getClientId(),
        page: this.kanbanPage,
        limit: this.kanbanLimit,
        where: taskWhereClause,
        order: { col: 'updated_at', dir: 'DESC' }
      };
      if (this.taskSearchQuery?.trim()) {
        taskPayload.search = this.taskSearchQuery.trim();
      }
      if (this.onlyOverdue) {
        taskPayload.startDate = '1970-01-01T00:00:00.000Z';
        taskPayload.endDate = new Date().toISOString();
      }

      this.taskService.getAllProjectTasks(taskPayload).subscribe({
        next: (res: any) => {
          const data = res?.data || res;
          const rawTasks = (data?.rows || data?.tasks || []).map((t: any) => ({ ...t, _itemType: 'task' }));
          
          this.kanbanTotal = data?.total ?? data?.count ?? data?.totalCount ?? rawTasks.length;

          let filtered = rawTasks;
          // Apply client-side sprint filtering
          if (this.sprintFilter === 'backlog') {
            filtered = rawTasks.filter((t: any) => !t.sprint_id || Number(t.sprint_id) === 0);
          } else if (this.sprintFilter && this.sprintFilter !== 'all') {
            filtered = rawTasks.filter((t: any) => Number(t.sprint_id) === Number(this.sprintFilter));
          }

          if (append) {
            this.allProjectTasks = [...this.allProjectTasks, ...filtered];
          } else {
            this.allProjectTasks = filtered;
          }
          this.unfilteredProjectTasks = [...this.allProjectTasks];

          this.hasMoreKanban = this.allProjectTasks.length < this.kanbanTotal && rawTasks.length === this.kanbanLimit;
          this.loadingAllTasks = false;
          this.loadingMore = false;
        },
        error: () => {
          this.loadingAllTasks = false;
          this.loadingMore = false;
          this.toastr.error('Failed to load tasks');
        }
      });

    } else {
      // 2. Bug filters
      const bugWhereClause: any = {
        project_id: this.projectId,
        client_id: this.localStorage.getClientId()
      };
      if (this.priorityFilter && this.priorityFilter !== 'all') {
        bugWhereClause.priority = this.priorityFilter;
      }
      if (this.assigneeFilter && this.assigneeFilter !== 'all') {
        bugWhereClause.assigned_to = this.assigneeFilter;
      }
      if (this.onlyMyTasks && this.currentUserId) {
        bugWhereClause.assigned_to = this.currentUserId;
      }

      const bugPayload: any = {
        user_id: this.localStorage.getUserId(),
        client_id: this.localStorage.getClientId(),
        page: (this.kanbanPage - 1) * this.kanbanLimit,
        limit: this.kanbanLimit,
        where: bugWhereClause,
        order: { col: '"bug"."updated_at"', dir: 'DESC' }
      };
      if (this.taskSearchQuery?.trim()) {
        bugPayload.filter = { title: this.taskSearchQuery.trim() };
      }

      this.bugService.getAllBugs(bugPayload).subscribe({
        next: (res: any) => {
          const data = res?.data || res;
          const rawBugs = (data?.rows || data?.bugs || []).map((b: any) => ({ ...b, _itemType: 'bug' }));
          
          this.kanbanTotal = data?.count ?? data?.total ?? data?.totalCount ?? rawBugs.length;

          let filtered = rawBugs;
          // Apply client-side sprint filtering
          if (this.sprintFilter === 'backlog') {
            filtered = rawBugs.filter((t: any) => !t.sprint_id || Number(t.sprint_id) === 0);
          } else if (this.sprintFilter && this.sprintFilter !== 'all') {
            filtered = rawBugs.filter((t: any) => Number(t.sprint_id) === Number(this.sprintFilter));
          }
          // Apply overdue filter client-side for bugs
          if (this.onlyOverdue) {
            filtered = filtered.filter((t: any) => {
              if (!t.due_date || t.status === 'closed' || t.status === 'resolved') return false;
              return new Date(t.due_date).getTime() < Date.now();
            });
          }

          if (append) {
            this.allProjectTasks = [...this.allProjectTasks, ...filtered];
          } else {
            this.allProjectTasks = filtered;
          }
          this.unfilteredProjectTasks = [...this.allProjectTasks];

          this.hasMoreKanban = this.allProjectTasks.length < this.kanbanTotal && rawBugs.length === this.kanbanLimit;
          this.loadingAllTasks = false;
          this.loadingMore = false;
        },
        error: () => {
          this.loadingAllTasks = false;
          this.loadingMore = false;
          this.toastr.error('Failed to load bugs');
        }
      });
    }
  }

  onFilterChange() {
    this.kanbanPage = 1;
    this.allProjectTasks = [];
    this.unfilteredProjectTasks = [];
    this.hasMoreKanban = false;
    this.loadTasks();
  }

  getTasksByStatus(status: string): any[] {
    const items = this.allProjectTasks || [];
    if (this.activeBoardTab === 'bugs') {
      return items.filter(t => t._itemType === 'bug' && (t.status || '').toLowerCase() === status.toLowerCase());
    } else {
      return items.filter(t => t._itemType === 'task' && (t.status || '').toLowerCase() === status.toLowerCase());
    }
  }

  getStatusClass(taskOrStatus: any): string {
    const s = typeof taskOrStatus === 'string' ? taskOrStatus.toLowerCase() : (taskOrStatus.status || '').toLowerCase();
    if (s === 'open' || s === 'reopen' || s === 're-open' || s === 'todo') return 'todo';
    if (s === 'closed' || s === 'resolved' || s === 'completed') return 'completed';
    return s;
  }

  getStatusLabel(status: string): string {
    const mapping: { [key: string]: string } = {
      todo: 'To Do',
      open: 'Open',
      reopen: 'Reopened',
      're-open': 'Re-Open',
      in_progress: 'In Progress',
      in_review: 'In Review',
      ready_for_test: 'Ready for Test',
      rejected: 'Rejected',
      resolved: 'Resolved',
      closed: 'Closed',
      blocked: 'Blocked',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return mapping[status] || this.prettyLabel(status);
  }

  prettyLabel(val: string): string {
    if (!val) return '';
    return val.replace(/_/g, ' ');
  }

  getPriorityClass(priority: string): string {
    if (!priority) return 'priority-low';
    return `priority-${priority.toLowerCase()}`;
  }

  getAvatarColor(user: any): string {
    if (!user || !user.fname) return '#6366f1';
    const colors = ['#ec4899', '#f43f5e', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b'];
    const idx = (user.fname.charCodeAt(0) + (user.lname?.charCodeAt(0) || 0)) % colors.length;
    return colors[idx];
  }

  getInitials(user: any): string {
    if (!user) return '?';
    const f = user.fname ? user.fname.charAt(0).toUpperCase() : '';
    const l = user.lname ? user.lname.charAt(0).toUpperCase() : '';
    return f + l;
  }

  isOverdue(task: any): boolean {
    if (task._itemType === 'bug') {
      if (!task.due_date || task.status === 'closed' || task.status === 'resolved') return false;
      return new Date(task.due_date).getTime() < new Date().getTime();
    }
    if (!task.due_date || task.status === 'completed') return false;
    return new Date(task.due_date).getTime() < new Date().getTime();
  }

  onDragDrop(event: CdkDragDrop<any[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      return;
    }

    const task = event.item.data;
    const oldStatus = task.status;
    const newStatus = event.container.id;

    let targetStatus = newStatus;

    // Optimistic UI update
    task.status = targetStatus;

    if (task._itemType === 'bug') {
      this.bugService.updateBug({
        bug_id: task.bug_id,
        where: { bug_id: task.bug_id },
        status: targetStatus,
        client_id: this.localStorage.getClientId()
      }).subscribe({
        next: () => {
          this.toastr.success(`Bug status updated to ${this.getStatusLabel(newStatus)}`);
          this.loadTasks();
        },
        error: () => {
          task.status = oldStatus;
          this.toastr.error('Failed to update bug status');
          this.loadTasks();
        }
      });
    } else {
      this.taskService.updateProjectTaskStatus({
        where: { task_id: task.task_id },
        status: targetStatus,
        client_id: this.localStorage.getClientId()
      }).subscribe({
        next: () => {
          this.toastr.success(`Task status updated to ${this.getStatusLabel(newStatus)}`);
          this.loadTasks();
        },
        error: () => {
          task.status = oldStatus;
          this.toastr.error('Failed to update task status');
          this.loadTasks();
        }
      });
    }
  }

  quickUpdateTaskStatus(task: any, status: string, event: Event) {
    event.stopPropagation();
    const prev = task.status;
    task.status = status;

    if (task._itemType === 'bug') {
      this.bugService.updateBug({
        bug_id: task.bug_id,
        where: { bug_id: task.bug_id },
        status: status,
        client_id: this.localStorage.getClientId()
      }).subscribe({
        next: () => {
          this.toastr.success(`Bug status updated to ${this.getStatusLabel(status)}`);
          this.loadTasks();
        },
        error: () => {
          task.status = prev;
          this.toastr.error('Failed to update bug status');
        }
      });
    } else {
      this.taskService.updateProjectTaskStatus({
        where: { task_id: task.task_id },
        status: status,
        client_id: this.localStorage.getClientId()
      }).subscribe({
        next: () => {
          this.toastr.success(`Task status updated to ${this.getStatusLabel(status)}`);
          this.loadTasks();
        },
        error: () => {
          task.status = prev;
          this.toastr.error('Failed to update task status');
        }
      });
    }
  }

  toggleCardStatusDdl(taskId: any, event: Event) {
    event.stopPropagation();
    if (this.cardStatusDdlOpenTaskId === taskId) {
      this.cardStatusDdlOpenTaskId = null;
    } else {
      this.cardStatusDdlOpenTaskId = taskId;
    }
  }

  closeCardStatusDdl() {
    this.cardStatusDdlOpenTaskId = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.closeCardStatusDdl();
  }

  scrollBoard(direction: 'left' | 'right') {
    const container = document.querySelector('.kanban-wrapper');
    if (container) {
      const scrollAmount = 320;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  }
}
